import React, { useState } from 'react';
import { Modal, Button } from '../ui';
import { getGearImageUrl, handleGearImageError } from '../../utils/GearImageHelpers';
import {
    EQUIPMENT_LIMITS,
    MAX_EQUIPPED_PER_PLAYER,
    getGearRejectionReason,
    getDisplacedGear,
} from '../../utils/gearRules';
import './GearItemCard.css';

// Position-based gear color constants (matching draft system)
const GEAR_POSITION_COLORS = {
  QB: '#f1c40f',        // Yellow
  RB: '#3498db',        // Blue
  WR: '#e74c3c',        // Red
  QB_RB: '#27ae60',     // Green (hybrid QB/RB)
  RB_WR: '#9b59b6',     // Purple (hybrid RB/WR)
  NONE: '#ffffff'       // White (no match)
};

// Position-based player button colors for modal
const PLAYER_POSITION_COLORS = {
  QB: '#f1c40f',        // Yellow
  RB: '#3498db',        // Blue
  WR: '#e74c3c',        // Red
};

/**
 * Analyzes gear description to determine which positions it applies to
 * @param {string} description - The gear's player_description or description
 * @returns {Object} - { isQB, isRB, isWR } boolean flags
 */
const analyzeGearPosition = (description) => {
  if (!description) return { isQB: false, isRB: false, isWR: false };
  
  const lowerDesc = description.toLowerCase();
  
  // QB keywords - anything with pass, completion, interception
  const isQB = /pass|completion|interception/i.test(lowerDesc);
  
  // RB keywords - anything with rush, carr (matches carry/carries)
  const isRB = /rush|carr/i.test(lowerDesc);
  
  // WR keywords - anything with reception, receiving, target, rec (short for reception)
  const isWR = /reception|receiving|target|\brec\b/i.test(lowerDesc);
  
  return { isQB, isRB, isWR };
};

/**
 * Gets the background color for a gear card based on position analysis
 * @param {Object} gear - The gear item object
 * @returns {string} - CSS color value
 */
const getGearPositionColor = (gear) => {
  const description = gear.player_description || gear.description || '';
  const { isQB, isRB, isWR } = analyzeGearPosition(description);
  
  // Hybrid cards (both positions match)
  if ((isQB && isRB) || (isQB && isWR)) return GEAR_POSITION_COLORS.QB_RB; // Green for QB/RB
  if (isRB && isWR) return GEAR_POSITION_COLORS.RB_WR; // Purple for RB/WR
  
  // Single position cards
  if (isQB) return GEAR_POSITION_COLORS.QB; // Yellow
  if (isRB) return GEAR_POSITION_COLORS.RB; // Blue
  if (isWR) return GEAR_POSITION_COLORS.WR; // Red
  
  // No match - keep white/default
  return GEAR_POSITION_COLORS.NONE;
};

/**
 * Gets the background color for a player button based on their position
 * @param {string} position - The player's position (QB, RB, WR)
 * @returns {string} - CSS color value
 */
const getPlayerPositionColor = (position) => {
  return PLAYER_POSITION_COLORS[position] || '#4a90e2'; // Default to blue if position not found
};

// Helper function to get rarity color (from DraftPage)
const getCardRarityColor = (rarity) => {
  switch (rarity) {
    case 'LEGENDARY': return '#FFD700'; // Gold
    case 'RARE': return '#9A4FFF'; // Purple
    case 'SILVER': return '#C0C0C0'; // Silver
    case 'BRONZE': return '#CD7F32'; // Bronze
    case 'COMMON': return '#8B8B8B'; // Gray
    default: return '#3498db'; // Blue
  }
};

// Helper function to get rarity border (from DraftPage)
const getCardRarityBorder = (rarity) => {
  switch (rarity) {
    case 'LEGENDARY': return '3px solid #FFD700'; // Gold border
    case 'RARE': return '3px solid #9A4FFF'; // Purple border
    case 'SILVER': return '3px solid #C0C0C0'; // Silver border
    case 'BRONZE': return '3px solid #CD7F32'; // Bronze border
    case 'COMMON': return '3px solid #8B8B8B'; // Gray border
    default: return '2px solid #ddd'; // Default border
  }
};

function GearItemCard({
  gear,
  gearNumber,
  canModifyGear,
  onUnequip,
  onEquip,
  teamPlayers,
  teamGear,
  onTransferClick,
  isInTransferMode,
  isTransferSource,
  isSwappable,
  onSwapClick
}) {
  const [showModal, setShowModal] = useState(false);

  // ⚠️ EQUIPMENT_LIMITS AND THE ELIGIBILITY RULE NOW COME FROM `utils/gearRules`.
  //
  // This component used to keep its own copy of the limits plus a local
  // `canPlayerEquipSlot()` that compared ONLY the candidate item's slot. It
  // never checked the 3-item total cap, so a player already holding three
  // items in OTHER slots was still offered an enabled button for a fourth —
  // and the equip died at the server with an error the user had been given no
  // reason to expect.
  //
  // `GearPage` had the correct two-part rule all along, which is exactly the
  // problem: two surfaces, two different contracts for one invariant. Both now
  // call the same `getGearRejectionReason()`.

  // Per-player, per-slot counts — still needed for the "1/2 HANDS" badge text.
  const getPlayerEquipmentCounts = (characterSheetId) => {
    const playerGear = (teamGear || []).filter(g => g.character_sheet === characterSheetId);
    const counts = {};

    Object.keys(EQUIPMENT_LIMITS).forEach(slot => {
      counts[slot] = 0;
    });

    playerGear.forEach(g => {
      if (counts[g.slot] !== undefined) {
        counts[g.slot]++;
      }
    });

    return counts;
  };

  /** Total items this player holds, across every slot. */
  const getTotalEquippedCount = (characterSheetId) =>
    (teamGear || []).filter(g => g.character_sheet === characterSheetId).length;

  const handleCardClick = () => {
    if (canModifyGear && (onUnequip || onEquip)) {
      setShowModal(true);
    }
  };

  const handleEquipToPlayer = (characterSheetId) => {
    if (onEquip) {
      onEquip(gear.id, characterSheetId);
    }
    setShowModal(false);
  };

  const handleUnequipGear = () => {
    if (onUnequip) {
      onUnequip(gear.id);
    }
    setShowModal(false);
  };

  const handleTransferButtonClick = (e) => {
    e.stopPropagation();
    
    if (!canModifyGear) return;
    
    // If this is a swappable gear item, execute swap
    if (isSwappable && onSwapClick) {
      onSwapClick(gear.id);
      return;
    }
    
    // If this is the source, cancel transfer mode by calling with null values
    if (isTransferSource && onTransferClick) {
      onTransferClick(null, null, null);
      return;
    }
    
    // Otherwise, start transfer mode
    if (onTransferClick) {
      onTransferClick(gear.id, gear.character_sheet, gear.slot);
    }
  };

  // ⚠️ IS THE "GEAR N" BADGE A CONTROL, OR JUST A LABEL?
  //
  // It is a TRANSFER button, and transfer only exists on GearPage, which is the
  // surface that can move an item between players. GearPage supplies
  // `onTransferClick` / `onSwapClick`; the PlayerPage gear tab supplies NEITHER,
  // because a single-player sheet has no second player to transfer to.
  //
  // Rendered unconditionally as a `<button>`, it was therefore a live, focusable,
  // fully-enabled control on PlayerPage whose every click path
  // (`handleTransferButtonClick`) hits a no-op: `isSwappable` is false,
  // `isTransferSource` is false, and `onTransferClick` is undefined, so the
  // function runs to completion and does nothing at all.
  //
  // That is worse than a disabled control. A disabled button explains itself; a
  // button that looks active, takes focus, announces itself to a screen reader,
  // and silently does nothing reads as a broken feature. The user cannot tell it
  // apart from an equip that failed.
  //
  // So the badge is a control ONLY where a handler exists to receive it, and is
  // a plain label everywhere else.
  const isTransferControl = Boolean(onTransferClick || onSwapClick);

  return (
    <>
      <div
        className={`gear-item-card ${isInTransferMode ? 'in-transfer-mode' : ''} ${isTransferSource ? 'transfer-source' : ''}`}
      >
        {/* Left side - Gear number badge. A transfer/swap button on GearPage;
            an inert label on surfaces that pass no transfer handler. */}
        {isTransferControl ? (
          <button
            className={`gear-number-badge clickable ${isTransferSource ? 'transfer-source' : ''} ${isSwappable ? 'swappable' : ''}`}
            onClick={handleTransferButtonClick}
            disabled={!canModifyGear || (isInTransferMode && !isTransferSource && !isSwappable)}
            type="button"
          >
            <span>GEAR {gearNumber}</span>
          </button>
        ) : (
          <div className="gear-number-badge">
            <span>GEAR {gearNumber}</span>
          </div>
        )}
        
        {/* Right side - Gear info (Info modal button) */}
        <button
          className="gear-info-box clickable"
          onClick={handleCardClick}
          disabled={!canModifyGear}
          type="button"
          style={{ backgroundColor: getGearPositionColor(gear) }}
        >
          <div className="gear-name">{gear.name}</div>
          <div className="gear-description">{gear.description}</div>
        </button>
      </div>

      {/* Modal for equip/unequip with DraftPage-style gear card */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={onUnequip ? "Unequip Gear" : "Equip to Player"}
        size="sm"
        showCloseButton={true}
      >
        {/* DraftPage-style Gear Card Display */}
        <div className="gear-card-container">
          <div
            className="gear-card-display"
            style={{
              width: '159px',
              height: '219px',
              position: 'relative',
              border: getCardRarityBorder(gear.rarity || 'COMMON'),
              borderRadius: 'var(--radius-md)',
              overflow: 'hidden',
              background: '#222',
              margin: '0 auto var(--spacing-md) auto'
            }}
          >
            {/* Gear Image */}
            <img
              src={getGearImageUrl(gear)}
              alt={gear.name}
              onError={handleGearImageError}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
            
            {/* Content Overlay */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.18)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: 'var(--spacing-xs)',
              color: 'white'
            }}>
              {/* Gear Name - Top */}
              <div style={{
                background: 'rgba(0,0,0,0.8)',
                padding: '4px 8px',
                borderRadius: 'var(--radius-sm)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-bold)',
                textAlign: 'center',
                marginTop: '-5px'
              }}>
                {gear.name}
              </div>
              
              {/* Bottom Section */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '3px'
              }}>
                {/* Item Description - Center */}
                {gear.description && (
                  <div style={{
                    background: 'rgba(255,255,255,0.9)',
                    padding: '4px 8px',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--font-size-xs)',
                    textAlign: 'center',
                    color: '#333',
                    fontWeight: '600',
                    maxWidth: '140px',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.2',
                    alignSelf: 'center'
                  }}>
                    {gear.description}
                  </div>
                )}

                {/* Rarity Badge - Bottom */}
                <div style={{
                  background: getCardRarityColor(gear.rarity || 'COMMON'),
                  padding: '4px 8px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-bold)',
                  textAlign: 'center'
                }}>
                  {gear.rarity || 'COMMON'}
                </div>
              </div>
            </div>

            {/* Slot Badge - Top Left Corner */}
            <div style={{
              position: 'absolute',
              top: '3px',
              left: '6px',
              background: 'rgba(255,255,255,0.9)',
              padding: '3px 4px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '8px',
              fontWeight: 'var(--font-weight-bold)',
              textAlign: 'center',
              color: '#333',
              lineHeight: '1.1',
              minWidth: '18px'
            }}>
              {gear.slot}
            </div>
          </div>
        </div>

        {/* Slot Info */}
        <div className="gear-modal-slot-info">
          SLOT LIMIT: {EQUIPMENT_LIMITS[gear.slot] || 1} {gear.slot} GEAR PIECE{EQUIPMENT_LIMITS[gear.slot] > 1 ? 'S' : ''} MAX PER PLAYER
        </div>

        {onUnequip ? (
          /* Unequip mode */
          <div className="gear-modal-actions">
            <Button
              variant="danger"
              fullWidth
              onClick={handleUnequipGear}
            >
              Unequip
            </Button>
            <Button
              variant="ghost"
              fullWidth
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
          </div>
        ) : (
          /* Equip mode */
          <div className="gear-modal-actions">
            <div className="gear-modal-player-list">
              {teamPlayers && teamPlayers.map(player => {
                // ONE SHARED RULE — enforces identity, the per-slot limit AND
                // the 3-item roster cap. The old local check only compared the
                // candidate's slot, which left a full player enabled for a
                // fourth item.
                //
                // `NO_CHARACTER_SHEET` is why this passes the raw field with no
                // `?? player.id` fallback: a LeaguePlayer id is a different
                // table's PK that the equip endpoint would happily resolve to
                // SOMEBODY ELSE'S sheet. An unidentifiable player is offered no
                // button at all.
                const rejectionReason = getGearRejectionReason(
                  teamGear, player.character_sheet_id, gear.slot
                );
                const canEquip = rejectionReason === null;

                const equipmentCounts = getPlayerEquipmentCounts(player.character_sheet_id);
                const currentSlotCount = equipmentCounts[gear.slot] || 0;
                const slotLimit = EQUIPMENT_LIMITS[gear.slot] || 1;
                const totalEquipped = getTotalEquippedCount(player.character_sheet_id);

                // ⚠️ WHAT WILL THIS EQUIP QUIETLY TAKE OFF?
                //
                // `GearEquipService.equip()` unequips EVERY item already in a
                // non-ANY candidate's slot. Where that collides with a limit
                // above 1 — `HANDS: 2` — the button was enabled, the badge read
                // "1/2 HANDS", and the equip then displaced the existing item:
                // the user was promised capacity and given replacement, losing
                // gear they were told there was room for.
                //
                // The action is still offered (replacing a worn item is
                // legitimate), but it is now NAMED before the click instead of
                // discovered afterwards.
                const displaced = canEquip
                  ? getDisplacedGear(teamGear, player.character_sheet_id, gear.slot)
                  : [];
                const displacedNames = displaced.map((item) => item.name).join(', ');

                // Say WHICH rule blocks them. "1/1 LEGS" next to a disabled
                // button is baffling when the real reason is a full roster —
                // and doubly so when the real reason is a broken record, where
                // the slot counts shown would not even be this player's.
                //
                // A pending displacement outranks the raw count: "1/2 HANDS"
                // is true but actively misleading when the equip will remove
                // that one item.
                const badgeText = rejectionReason === 'NO_CHARACTER_SHEET'
                  ? 'UNAVAILABLE'
                  : rejectionReason === 'ROSTER_FULL'
                    ? `FULL ${totalEquipped}/${MAX_EQUIPPED_PER_PLAYER}`
                    : displaced.length > 0
                      ? `REPLACES ${gear.slot}`
                      : `${currentSlotCount}/${slotLimit} ${gear.slot}`;

                const playerColor = getPlayerPositionColor(player.position);

                return (
                  <button
                    key={player.id}
                    className={`gear-modal-player-button ${!canEquip ? 'disabled' : ''}`}
                    onClick={() => canEquip && handleEquipToPlayer(player.character_sheet_id)}
                    disabled={!canEquip}
                    title={
                      rejectionReason === 'NO_CHARACTER_SHEET'
                        ? `${player.name}'s record is missing its character sheet, so gear cannot be equipped to them`
                        : rejectionReason === 'ROSTER_FULL'
                          ? `${player.name} already holds the maximum of ${MAX_EQUIPPED_PER_PLAYER} items`
                          : rejectionReason === 'SLOT_FULL'
                            ? `${player.name}'s ${gear.slot} slot is full (${slotLimit} max)`
                            : displaced.length > 0
                              ? `Equipping this will UNEQUIP ${displacedNames} — ` +
                                `only one ${gear.slot} item can be worn at a time`
                              : undefined
                    }
                    style={{
                      background: canEquip ? playerColor : 'var(--color-bg-tertiary)',
                      borderColor: canEquip ? playerColor : 'var(--color-danger)'
                    }}
                  >
                    <span className="player-name">{player.name}</span>
                    <span className="player-slot-count">
                      {badgeText}
                    </span>
                  </button>
                );
              })}
            </div>
            <Button
              variant="ghost"
              fullWidth
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
          </div>
        )}
      </Modal>
    </>
  );
}

export default GearItemCard;